/**
 * Shared Spanish labels for the AI module (client and admin pages).
 */

export const categoryLabels: Record<string, string> = {
  cost_optimization: 'Optimización de costes',
  adoption: 'Adopción',
  security: 'Seguridad',
  compliance: 'Cumplimiento',
}

export const originLabels: Record<string, string> = {
  rule: 'Regla',
  ai_enriched: 'IA enriquecida',
  ai_generated: 'Investigación IA',
}

export const findingKindLabels: Record<string, string> = {
  new: 'Hallazgo nuevo',
  extension: 'Amplía una regla',
  confirmation: 'Confirma una regla',
  data_gap: 'Falta validar',
  legacy: 'Formato anterior',
}

export const findingKindVariant: Record<string, 'success' | 'warning' | 'error' | 'info' | 'neutral' | 'premium'> = {
  new: 'premium',
  extension: 'info',
  confirmation: 'neutral',
  data_gap: 'warning',
  legacy: 'neutral',
}

export const savingStatusLabels: Record<string, string> = {
  computed: 'Ahorro verificable',
  estimated_catalog: 'Estimación (catálogo)',
  pending_valuation: 'Pendiente de valorar',
  not_applicable: 'Sin cuantificar',
}

export const savingStatusVariant: Record<string, 'success' | 'warning' | 'error' | 'info' | 'neutral' | 'premium'> = {
  computed: 'success',
  estimated_catalog: 'info',
  pending_valuation: 'warning',
  not_applicable: 'neutral',
}

export const researchKindLabels: Record<string, string> = {
  sku_overlap: 'Solapamiento de SKUs',
  sku_alternative: 'SKU alternativo',
  premium_underuse: 'Premium infrautilizada',
  capability_gap: 'Funcionalidades no usadas',
  catalog_change: 'Cambios de catálogo',
  rule_extension: 'Ampliación de regla',
  synthetic_test: 'Prueba sintética',
}

export const taskStatusLabels: Record<string, string> = {
  pending: 'Pendiente',
  running: 'En ejecución',
  completed: 'Completada',
  needs_review: 'Requiere revisión',
  rejected: 'Descartada',
  error: 'Error',
  cancelled: 'Cancelada',
  skipped_cap: 'Fuera de cupo',
  skipped_policy: 'Suprimida (decisión previa)',
  skipped_budget: 'Sin presupuesto',
}

export const taskStatusVariant: Record<string, 'success' | 'warning' | 'error' | 'info' | 'neutral' | 'premium'> = {
  pending: 'neutral',
  running: 'info',
  completed: 'success',
  needs_review: 'warning',
  rejected: 'neutral',
  error: 'error',
  cancelled: 'neutral',
  skipped_cap: 'neutral',
  skipped_policy: 'neutral',
  skipped_budget: 'warning',
}

export const runStatusLabels: Record<string, string> = {
  queued: 'En cola',
  running: 'En ejecución',
  completed: 'Completado',
  partial: 'Parcial',
  failed: 'Fallido',
  cancelled: 'Cancelado',
}

export const runStatusVariant: Record<string, 'success' | 'warning' | 'error' | 'info' | 'neutral'> = {
  completed: 'success',
  partial: 'warning',
  failed: 'error',
  running: 'info',
  queued: 'neutral',
  cancelled: 'neutral',
}

export const actionLabels: Record<string, string> = {
  downgrade: 'Cambiar a un SKU inferior',
  remove_license: 'Retirar licencia',
  remove_redundant: 'Retirar licencia redundante',
  reassign: 'Reasignar',
  disable_service: 'Deshabilitar servicio',
  renegotiate: 'Renegociar contrato',
  review: 'Revisar',
  validate_with_client: 'Requiere validación por vuestra parte',
  none: 'Sin acción',
}

export const severityLabels: Record<string, string> = {
  critical: 'Crítica',
  high: 'Alta',
  medium: 'Media',
  low: 'Baja',
}

export function parseJsonArray<T = string>(json: unknown): T[] {
  if (Array.isArray(json)) return json as T[]
  if (typeof json !== 'string') return []
  try {
    const v = JSON.parse(json) as unknown
    return Array.isArray(v) ? (v as T[]) : []
  } catch {
    return []
  }
}

export function parseJsonObject<T extends object>(json: unknown): T | null {
  if (json && typeof json === 'object') return json as T
  if (typeof json !== 'string') return null
  try {
    const v = JSON.parse(json) as unknown
    return v && typeof v === 'object' ? (v as T) : null
  } catch {
    return null
  }
}

/** Where a piece of tenant evidence came from (agent tool names -> plain words). */
export const evidenceSourceLabels: Record<string, string> = {
  context: 'contexto inicial del tenant',
  get_sku_inventory: 'inventario de licencias',
  get_sku_usage_profile: 'perfil de uso de la licencia',
  get_users_by_pattern: 'grupo de usuarios por patrón de uso',
  get_rule_recommendations: 'recomendaciones del motor de reglas',
  get_internal_pricing: 'precios internos',
  get_prior_decisions: 'decisiones previas',
  get_service_plans: 'planes de servicio de la licencia',
}

/** Internal identifiers the investigator sometimes echoes -> plain Spanish. Longest keys first so partial matches never win. */
const technicalTerms: Array<[RegExp, string]> = [
  [/\bmonthly_cost_per_unit\b/g, 'precio contractual por unidad'],
  [/\bcontract_price\b/g, 'precio contractual'],
  [/\bcatalog_price\b/g, 'precio de catálogo'],
  [/\bbilling_plan\b/g, 'tipo de pago'],
  [/\bexpires_at\b/g, 'fecha de vencimiento'],
  [/\bservice_plans?\b/g, 'planes de servicio'],
  [/\bassigned_units\b/g, 'unidades asignadas'],
  [/\bavailable_units\b/g, 'unidades disponibles'],
  [/\btotal_units\b/g, 'unidades contratadas'],
  [/\bactivity_report\b/g, 'informe de actividad'],
  [/\blast_sync_at\b/g, 'fecha de la última sincronización'],
  [/\bdata_as_of\b/g, 'fecha de los datos'],
  [/\bdisabled_plans\b/g, 'planes deshabilitados'],
  [/\bassignment_source\b/g, 'origen de la asignación'],
  [/\bneeds_data\b/g, 'faltan datos'],
  [/\bneeds_review\b/g, 'requiere revisión'],
  [/\brule_extension\b/g, 'ampliación de una regla'],
  [/\bsku_overlap\b/g, 'solapamiento de licencias'],
  [/\bpremium_underuse\b/g, 'licencia premium infrautilizada'],
  [/\bsku_alternative\b/g, 'licencia alternativa'],
  [/\bcapability_gap\b/g, 'funcionalidades sin usar'],
  [/\bcatalog_change\b/g, 'cambio de catálogo'],
  [/\bremove_redundant\b/g, 'retirar licencia redundante'],
  [/\bremove_license\b/g, 'retirar licencia'],
  [/\breduce_purchased\b/g, 'reducir unidades compradas'],
  [/\bdisable_service\b/g, 'deshabilitar servicio'],
  [/\bdowngrade\b/g, 'cambio a una licencia inferior'],
  [/\bnull\b/g, 'sin dato'],
]

/** Keeps the capitalisation of the original match on the replacement. */
function keepCase(match: string, replacement: string): string {
  return /^[A-ZÁÉÍÓÚÑ]/.test(match) ? replacement.charAt(0).toUpperCase() + replacement.slice(1) : replacement
}

/**
 * The reader IS the client: findings written before prompt v1.4 addressed a
 * consultant ("validar con el cliente"). Rewritten to second person plural.
 */
const clientVoiceRules: Array<[RegExp, (...m: string[]) => string]> = [
  // "validar(lo) [el motivo] con el cliente" -> "validar(lo) [el motivo] por vuestra parte" (up to 4 words in between)
  [/\b(validar|confirmar|contrastar|comprobar|revisar|verificar)(lo|la|los|las)?((?:\s+(?!con\b)\S+){0,4})\s+con el cliente\b/gi, (_m, verb, encl, mid) => `${verb}${encl ?? ''}${mid ?? ''} por vuestra parte`],
  [/\b(consultar)(lo|la|los|las)?((?:\s+(?!con\b|al\b)\S+){0,4})\s+(?:con el|al) cliente\b/gi, (m, _verb, encl, mid) => keepCase(m, `confirmar${encl ?? ''}${mid ?? ''} por vuestra parte`)],
  [/\b(validación|confirmación|comprobación) con el cliente\b/gi, (_m, noun) => `${noun} por vuestra parte`],
  [/\bel cliente debe\b/gi, (m) => keepCase(m, 'debéis')],
  [/\bel cliente debería\b/gi, (m) => keepCase(m, 'deberíais')],
  [/\bel cliente tiene que\b/gi, (m) => keepCase(m, 'tenéis que')],
  [/\bel cliente necesita\b/gi, (m) => keepCase(m, 'necesitáis')],
]

/**
 * Turns investigator text into plain Spanish: replaces tool names, internal
 * field names and SKU codes (by the license's display name when known).
 * `skuNames` maps UPPER(sku_part_number) -> name shown to the client.
 */
export function humanizeFindingText(text: string | null | undefined, skuNames?: Map<string, { name: string }>): string {
  if (!text) return ''
  let out = text
  for (const [name, label] of Object.entries(evidenceSourceLabels)) out = out.replace(new RegExp(`\\b${name}\\b`, 'g'), label)
  for (const [re, label] of technicalTerms) out = out.replace(re, label)
  for (const [re, fn] of clientVoiceRules) out = out.replace(re, fn)
  // Backend placeholders in missing_data: "contract_price:SPE_E3" -> "precio contractual de <nombre>".
  out = out.replace(/\b(precio contractual|planes de servicio):([A-Z0-9_]+)\b/g, (_m, label: string, sku: string) => `${label} de ${skuNames?.get(sku)?.name ?? sku}`)
  if (skuNames && skuNames.size > 0) {
    // SKU part numbers are upper-case tokens with digits or underscores (SPE_E3, POWERAPPS_PER_USER, EMSPREMIUM).
    out = out.replace(/\b[A-Z][A-Z0-9_]{3,}\b/g, (token) => skuNames.get(token)?.name ?? token)
  }
  return out
}

